import { z } from "zod";
import { decimalCompare, decimalSchema } from "../values/decimal";

import { insertInventoryTransactionSchema, InventoryTransaction } from "../platform/schema";
import {
  getDefaultInventoryApplied as getTransactionDefaultInventoryApplied,
  isInventoryAppliedEditable,
  requiresFinalCountedAmount,
} from "./transaction-types";
import { TRANSACTION_MESSAGES } from "./inventory-copy";

const transactionWorkflowBaseSchema = insertInventoryTransactionSchema.extend({
  quantity: decimalSchema.optional(),
  finalCountedAmount: decimalSchema.optional(),
  recordOnly: z.boolean().optional(),
  transactionDate: z.coerce.date().optional(),
});

export const transactionWorkflowSchema = transactionWorkflowBaseSchema.superRefine((data, ctx) => {
  if (requiresFinalCountedAmount(data.transactionType)) {
    if (data.finalCountedAmount == null || decimalCompare(data.finalCountedAmount, "0") < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: TRANSACTION_MESSAGES.finalCountedRequired,
        path: ["finalCountedAmount"],
      });
    }
    return;
  }

  if (data.quantity == null || decimalCompare(data.quantity, "0") <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: TRANSACTION_MESSAGES.quantityPositive,
      path: ["quantity"],
    });
  }

});

export const updateTransactionWorkflowSchema = transactionWorkflowBaseSchema
  .omit({ createdBy: true })
  .partial()
  .superRefine((data, ctx) => {
    if (data.finalCountedAmount != null && decimalCompare(data.finalCountedAmount, "0") < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: TRANSACTION_MESSAGES.finalCountedNonnegative,
        path: ["finalCountedAmount"],
      });
    }

    if (
      data.quantity != null &&
      !requiresFinalCountedAmount(data.transactionType ?? "") &&
      decimalCompare(data.quantity, "0") <= 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: TRANSACTION_MESSAGES.quantityPositive,
        path: ["quantity"],
      });
    }
  });

export function serializeHistoryValue(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

export function getDefaultInventoryApplied(transactionType: string): boolean {
  return getTransactionDefaultInventoryApplied(transactionType);
}

export function normalizeInventoryApplied(transactionType: string, inventoryApplied?: boolean | null): boolean {
  if (isInventoryAppliedEditable(transactionType)) {
    return inventoryApplied ?? false;
  }

  return getTransactionDefaultInventoryApplied(transactionType);
}

export function buildTransactionPayload(
  source: Record<string, any>,
  createdBy: number,
  tenantId: number,
) {
  return {
    chemicalId: source.chemicalId,
    transactionType: source.transactionType,
    inventoryApplied: source.recordOnly ? false : normalizeInventoryApplied(source.transactionType, source.inventoryApplied),
    quantity: source.quantity,
    finalCountedAmount: source.finalCountedAmount ?? null,
    unit: source.unit,
    transactionDate: source.transactionDate,
    supplierId: source.supplierId ?? null,
    supplierContactId: source.supplierContactId ?? null,
    purchaserId: source.purchaserId ?? null,
    purchaserContactId: source.purchaserContactId ?? null,
    price: source.price ?? null,
    referenceNumber: source.referenceNumber ?? null,
    supplierBatchNumber: source.supplierBatchNumber ?? null,
    usageDetails: source.usageDetails ?? null,
    disposalReason: source.disposalReason ?? null,
    notes: source.notes ?? null,
    createdBy,
    tenantId,
  };
}

export function mergeTransactionUpdate(
  existing: InventoryTransaction,
  updates: Record<string, any>,
) {
  return {
    ...existing,
    ...updates,
    inventoryApplied: normalizeInventoryApplied(
      updates.transactionType ?? existing.transactionType,
      updates.inventoryApplied ?? existing.inventoryApplied,
    ),
  };
}
